/**
 * Session API Service
 *
 * File: Mythbound.App/src/services/api/SessionService.ts
 * Created: 2026-01-06
 * Last Modified: 2026-09-22 (PRD001A 001A.5 — session frontend)
 *
 * PURPOSE:
 * HTTP client for session lifecycle management (start/pause/resume/end) and state polling.
 * Coordinates with SessionContext for real-time session state updates.
 *
 * WHO: `SessionContext.tsx` (all methods), `SessionControls.tsx` (error-code
 * helpers, for its own local error banner).
 * WHAT: Every method below takes an optional trailing `{ signal }` so
 * `SessionContext` can cancel an in-flight request (mount-scoped fetch, or
 * the polling loop's periodic `getSessionState` call) on unmount or campaign
 * change, and throws a structured `ApiError` (see `ApiError.ts`) instead of a
 * generic `Error`.
 * WHY (PRD001A 001A.7a / 001A.4 Wave 3): same reasoning as `CharacterService.ts`
 * — a 503/network failure must be distinguishable from a 404 by `.kind`, and
 * a non-JSON error body must not itself throw a confusing parse error.
 *
 * REAL PAUSE/RESUME POLICY (PRD001A 001A.5, enforced by the backend, mirrored
 * here only as caller-specific response flags — never re-derived client-side):
 * ANY verified current campaign participant (any role) may pause an 'active'
 * session. Only the recorded pauser OR the current GM may resume a 'paused'
 * session. The backend's `SessionActionResponse`/`SessionState` responses
 * carry `can_pause`/`can_resume`/`paused_by_me` computed server-side for the
 * calling user — `SessionControls.tsx` renders buttons from those flags
 * directly and never re-derives them from an `isGM` prop or a locally-tracked
 * role guess.
 *
 * MULTI-PLATFORM SYNC:
 * - Webapp "Play Myth" button calls startSession
 * - Discord /mythbound start calls same backend endpoint
 * - Both result in same session active state
 * - WebSocket broadcasts to all participants instantly
 *
 * POLLING STRATEGY:
 * - Non-critical updates use 2-5s polling (HP changes, inventory updates)
 * - Critical events use WebSocket instant (session start/end, participant joined, character created)
 *
 * AUTOSAVE:
 * - Backend autosaves every 5 minutes
 * - 6-minute crash buffer for recovery
 * - Frontend polls session state every 3 seconds (middle of 2-5s range)
 *
 * Pipeline: Frontend session UI → SessionService → Backend session manager → Game state persistence
 * Dependencies: authorizedFetch (cookie + bearer-if-fresh), ApiError
 */

import { authorizedFetch } from './authTransport';
import { apiErrorFromResponse, apiErrorFromNetworkFailure, ApiError } from './ApiError';
import { getApiBaseUrl } from '../../utils/environment';

export interface Session {
  session_id: string;
  campaign_id: string;
  status: 'scheduled' | 'active' | 'paused' | 'ended';
  started_at: string | null;
  ended_at: string | null;
  started_by: string | null;
  duration_minutes: number | null;
  /** Persisted mutation cursor; missing values are never replaced with a guessed zero. */
  lifecycle_revision?: number;
  master_mode_enabled?: boolean;
}

/**
 * GET /api/sessions/{id}/state response (contract:
 * shared/contracts/session/v1/session-management.yaml, `get_session_state`).
 *
 * The state and status fields use the same canonical lifecycle values.
 * Master Mode is a separate persisted session setting. Elapsed seconds and
 * minutes already exclude every paused interval; the frontend must not add
 * its own paused-time compensation.
 * `can_pause`/`can_resume`/`paused_by_me` are caller-specific and
 * server-computed; `paused_by_user_id` itself is never serialized.
 */
export interface SessionState {
  session_id: string;
  campaign_id: string;
  status: 'scheduled' | 'active' | 'paused' | 'ended';
  state?: SessionActionState;
  started_at?: string | null;
  elapsed_minutes: number;
  elapsed_seconds: number;
  /** Explicit persisted setting; pause/resume preserves it and session end clears it. */
  master_mode_enabled?: boolean;
  can_pause: boolean;
  can_resume: boolean;
  paused_by_me: boolean;
  /** Version required by every consequential session lifecycle/mode command. */
  lifecycle_revision?: number;
  current_turn?: number;
  current_player_turn?: string;
  round_number?: number;
  /** Campaign membership roles returned by GET /sessions/{id}/state. */
  participants?: Array<{ user_id: string; role: string }>;
}

export interface SessionStartRequest {
  source: 'webapp' | 'discord';
  /** Captured campaign cursor closes active-A -> ended-A races at commit time. */
  expected_current_session_id: string | null;
  expected_is_session_active: boolean;
}

export interface SessionEndRequest {
  expected_revision: number;
  session_summary?: string;
}

export type SessionActionState = 'active' | 'paused' | 'ended';

/**
 * Response shape for start/pause/resume/end. `can_pause`/`can_resume`/
 * `paused_by_me` are caller-specific and server-computed (see `SessionState`
 * above) — always `false` on `endSession`'s response, since an ended session
 * can no longer be paused or resumed by anyone.
 */
export interface SessionActionResponse {
  session_id: string;
  state: SessionActionState;
  message: string;
  can_pause: boolean;
  can_resume: boolean;
  paused_by_me: boolean;
  master_mode_enabled: boolean;
  lifecycle_revision: number;
}

/** POST /api/events/sessions/{id}/emergency-save response. */
export interface EmergencySaveResponse {
  success: boolean;
  saved_at?: string | null;
  error?: string | null;
  throttle_remaining_seconds?: number | null;
  message?: string | null;
}

/** Every method accepts this optional bag; currently only `signal` is used. */
export interface SessionRequestOptions {
  signal?: AbortSignal;
}

function requireExpectedRevision(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error('A current session revision is required. Refresh and review session state before trying again.');
  }
  return value;
}

function parseActionResponse(body: unknown, responseStatus: number, sessionId: string): SessionActionResponse {
  const invalidResponse = () => new ApiError('Invalid session action response', responseStatus, 'unknown', body);
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw invalidResponse();
  const response = body as Record<string, unknown>;
  if (response.session_id !== sessionId || !['active', 'paused', 'ended'].includes(String(response.state)) ||
      typeof response.message !== 'string' || typeof response.can_pause !== 'boolean' ||
      typeof response.can_resume !== 'boolean' || typeof response.paused_by_me !== 'boolean' ||
      typeof response.master_mode_enabled !== 'boolean' || !Number.isSafeInteger(response.lifecycle_revision) ||
      (response.lifecycle_revision as number) < 0) throw invalidResponse();
  return body as SessionActionResponse;
}

/**
 * HTTP start/end callers use this boundary before installing a Session in the
 * shared provider. Reject mismatched identity or lifecycle status so a 2xx
 * response cannot acknowledge another campaign/session or a queued action.
 * Preserve the response's nullable facts; the handler does not return ended_at.
 */
function parseLifecycleSession(
  body: unknown,
  responseStatus: number,
  expected: { campaignId: string; sessionId?: string; status: 'active' | 'ended' }
): Session {
  const invalidResponse = () => new ApiError('Invalid session lifecycle response', responseStatus, 'unknown', body);
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw invalidResponse();

  const response = body as Record<string, unknown>;
  const nullableString = (value: unknown) => value === null || typeof value === 'string';
  const duration = response.duration_minutes;
  const revision = response.lifecycle_revision;
  if (
    typeof response.session_id !== 'string' || response.session_id.trim().length === 0 ||
    typeof response.campaign_id !== 'string' || response.campaign_id.trim().length === 0 ||
    response.campaign_id !== expected.campaignId ||
    (expected.sessionId !== undefined && response.session_id !== expected.sessionId) ||
    response.status !== expected.status ||
    !Number.isSafeInteger(revision) || (revision as number) < 0 ||
    !nullableString(response.started_at) ||
    !nullableString(response.started_by) ||
    (duration !== undefined && duration !== null && (!Number.isInteger(duration) || (duration as number) < 0))
  ) {
    throw invalidResponse();
  }

  return {
    session_id: response.session_id as string,
    campaign_id: response.campaign_id as string,
    status: response.status as Session['status'],
    started_at: response.started_at as string | null,
    // SessionResponse deliberately omits ended_at; this field is not part of
    // the lifecycle acknowledgment and is not fabricated from client time.
    ended_at: null,
    started_by: response.started_by as string | null,
    duration_minutes: (duration ?? null) as number | null,
    lifecycle_revision: revision as number,
  };
}

function parseEmergencySaveResponse(body: unknown, status: number): EmergencySaveResponse {
  const invalidResponse = () => new ApiError('Invalid emergency save response', status, 'unknown', body);
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw invalidResponse();

  const response = body as Record<string, unknown>;
  const isOptionalString = (value: unknown) => value === undefined || value === null || typeof value === 'string';
  const throttle = response.throttle_remaining_seconds;
  if (
    typeof response.success !== 'boolean' ||
    !isOptionalString(response.saved_at) ||
    !isOptionalString(response.error) ||
    !isOptionalString(response.message) ||
    (throttle !== undefined && throttle !== null &&
      (typeof throttle !== 'number' || !Number.isFinite(throttle) || throttle < 0))
  ) {
    throw invalidResponse();
  }

  if (response.success === true && (
    typeof response.saved_at !== 'string' ||
    response.saved_at.trim().length === 0 ||
    !Number.isFinite(Date.parse(response.saved_at))
  )) {
    throw invalidResponse();
  }

  return body as EmergencySaveResponse;
}

/**
 * Machine-readable error codes the session contract defines, all carried in
 * the response body as `{"detail": {"message": "...", "code": "..."}}` (same
 * shape as the auth/Character error contracts). Several share an HTTP status
 * (e.g. `session_not_active`/`session_ended`/`already_paused_by_other`/
 * `session_already_active` are all 409) — `ApiError.kind` alone cannot
 * distinguish them; use `getSessionErrorCode`/`getSessionErrorMessage`.
 */
export type SessionErrorCode =
  | 'not_found'
  | 'forbidden'
  | 'session_not_active'
  | 'session_ended'
  | 'already_paused_by_other'
  | 'session_already_active'
  | 'pause_first'
  | 'stale_state';

const KNOWN_SESSION_ERROR_CODES: SessionErrorCode[] = [
  'not_found',
  'forbidden',
  'session_not_active',
  'session_ended',
  'already_paused_by_other',
  'session_already_active',
  'pause_first',
  'stale_state',
];

/**
 * Extract the session contract's `detail.code` from a thrown `ApiError`, if
 * present. Returns `null` for a non-`ApiError`, a body with no nested
 * `detail.code`, or a code this contract doesn't define (never throws).
 */
export function getSessionErrorCode(error: unknown): SessionErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.detail;
  if (!body || typeof body !== 'object') return null;
  const nested = (body as Record<string, unknown>).detail;
  if (!nested || typeof nested !== 'object') return null;
  const code = (nested as Record<string, unknown>).code;
  return typeof code === 'string' && (KNOWN_SESSION_ERROR_CODES as string[]).includes(code)
    ? (code as SessionErrorCode)
    : null;
}

/**
 * Honest, per-code messages for the 409 conflict codes (item 3, 001A.5). The
 * backend's own `detail.message` is deliberately a "fixed, generic,
 * non-identifying string" (contract, `error_contract.shape`) — it is not
 * guaranteed to read as a clear, user-facing sentence. This mapping is the
 * frontend's own honest text, keyed by the machine `code`, so a caller (e.g.
 * `SessionControls`) shows something meaningful instead of the raw backend
 * string. Falls back to the `ApiError`'s own message for any other error.
 */
const SESSION_ERROR_MESSAGES: Record<SessionErrorCode, string> = {
  // PRD002 deliberately uses one code for absent and inaccessible sessions.
  // Avoid claiming deletion when current campaign membership may be missing.
  not_found: 'This session is not available.',
  forbidden: "You don't have permission to do that for this session.",
  session_not_active: "This session isn't active right now, so it can't be paused.",
  session_ended: 'This session has already ended.',
  already_paused_by_other: 'Someone else already paused this session.',
  session_already_active: 'This campaign already has an active or paused session.',
  pause_first: 'Pause the session before enabling Master Mode.',
  stale_state: 'Session state changed. Review the refreshed state before trying this action again.',
};

/**
 * Map a thrown error to an honest, session-contract-aware message: a known
 * `SessionErrorCode` gets its `SESSION_ERROR_MESSAGES` text; any other
 * `ApiError` falls back to its own `.message`; anything else falls back to
 * `fallbackMessage`.
 */
export function getSessionErrorMessage(error: unknown, fallbackMessage: string): string {
  const code = getSessionErrorCode(error);
  if (code) return SESSION_ERROR_MESSAGES[code];
  if (error instanceof ApiError) return error.message;
  return fallbackMessage;
}

export class SessionService {
  private apiBaseUrl = getApiBaseUrl();

  /**
   * Request an emergency save and return only a validated server response.
   * A successful HTTP status alone is not confirmation: success requires
   * success: true and a parseable saved_at timestamp.
   */
  async emergencySave(
    sessionId: string,
    requestedBy: string,
    options?: SessionRequestOptions
  ): Promise<EmergencySaveResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(
        this.apiBaseUrl + '/events/sessions/' + encodeURIComponent(sessionId) + '/emergency-save',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requested_by: requestedBy, channel: 'webapp' }),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to save session');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to save session');

    let body: unknown;
    try {
      body = await response.json();
    } catch (error) {
      throw new ApiError('Invalid emergency save response', response.status, 'unknown', error);
    }
    return parseEmergencySaveResponse(body, response.status);
  }

  /**
   * Start session from webapp or Discord MM™
   *
   * @throws {ApiError} on any non-ok response or network failure
   */
  async startSession(campaignId: string, request: SessionStartRequest, options?: SessionRequestOptions): Promise<Session> {
    if (!(request.expected_current_session_id === null || (typeof request.expected_current_session_id === 'string' && request.expected_current_session_id.length > 0)) || typeof request.expected_is_session_active !== 'boolean') {
      throw new Error('A confirmed campaign cursor is required before starting a session.');
    }
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/campaigns/${encodeURIComponent(campaignId)}/sessions/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: request.source,
            expected_current_session_id: request.expected_current_session_id,
            expected_is_session_active: request.expected_is_session_active,
          }),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to start session');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to start session');
    let body: unknown;
    try {
      body = await response.json();
    } catch (error) {
      throw new ApiError('Invalid session lifecycle response', response.status, 'unknown', error);
    }
    return parseLifecycleSession(body, response.status, { campaignId, status: 'active' });
  }

  /**
   * End session and record completion
   *
   * @throws {ApiError} on any non-ok response or network failure
   */
  async endSession(sessionId: string, request: SessionEndRequest, options?: SessionRequestOptions): Promise<Session> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/sessions/${encodeURIComponent(sessionId)}/end`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            expected_revision: requireExpectedRevision(request.expected_revision),
            ...(request.session_summary !== undefined ? { session_summary: request.session_summary } : {}),
          }),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to end session');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to end session');
    let body: unknown;
    try {
      body = await response.json();
    } catch (error) {
      throw new ApiError('Invalid session lifecycle response', response.status, 'unknown', error);
    }
    const raw = body && typeof body === 'object' ? body as Record<string, unknown> : null;
    if (!raw || typeof raw.campaign_id !== 'string' || raw.campaign_id.trim().length === 0) {
      throw new ApiError('Invalid session lifecycle response', response.status, 'unknown', body);
    }
    return parseLifecycleSession(body, response.status, { campaignId: raw.campaign_id, sessionId, status: 'ended' });
  }

  /**
   * Get current session state via polling (2-5 second interval)
   *
   * @param options Optional `{ signal }` — `SessionContext`'s polling loop
   *   passes a fresh `AbortSignal` per tick so a stale poll response arriving
   *   after the campaign changed (or the session ended) is ignored/aborted.
   * @throws {ApiError} on any non-ok response or network failure
   */
  async getSessionState(sessionId: string, options?: SessionRequestOptions): Promise<SessionState> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/sessions/${sessionId}/state`,
        { method: 'GET', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to get session state');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to get session state');
    return response.json();
  }

  /**
   * Pause an active session.
   *
   * Real policy (backend-enforced, PRD001A 001A.5): ANY verified current
   * campaign participant — any role, not just the GM — may pause an
   * 'active' session. The pause owner is always the authenticated caller;
   * this method never sends an actor/role field. A retry by the same actor
   * is an idempotent success, not an error. On a conflict (e.g. the session
   * is not active, or already ended) the backend returns 409 with a machine
   * `code` — see `getSessionErrorCode`/`getSessionErrorMessage` above.
   *
   * The configured backend route is POST /api/events/sessions/{session_id}/pause.
   *
   * @throws {ApiError} on any non-ok response or network failure
   */
  async pauseSession(sessionId: string, expectedRevision: number, options?: SessionRequestOptions): Promise<SessionActionResponse> {
    const expected_revision = requireExpectedRevision(expectedRevision);
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/events/sessions/${sessionId}/pause`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_revision }), signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to pause session');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to pause session');
    return parseActionResponse(await response.json(), response.status, sessionId);
  }

  /**
   * Resume a paused session.
   *
   * Real policy (backend-enforced, PRD001A 001A.5): ONLY the recorded pauser
   * (the same user who paused it) OR the current GM may resume a 'paused'
   * session — nobody else, regardless of role. A legacy row with no recorded
   * pauser can only be resumed by a verified GM. A retry by the same actor
   * is an idempotent success. See `pauseSession` above for the 409 code
   * helpers.
   *
   * The configured backend route is POST /api/events/sessions/{session_id}/resume.
   *
   * @throws {ApiError} on any non-ok response or network failure
   */
  async resumeSession(sessionId: string, expectedRevision: number, options?: SessionRequestOptions): Promise<SessionActionResponse> {
    const expected_revision = requireExpectedRevision(expectedRevision);
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/events/sessions/${sessionId}/resume`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_revision }), signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to resume session');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to resume session');
    return parseActionResponse(await response.json(), response.status, sessionId);
  }
  /**
   * Explicitly set the persisted Master Mode flag for this session.
   * The authenticated account is derived by the backend; this request only
   * carries the requested value.
   */
  async setMasterMode(sessionId: string, enabled: boolean, expectedRevision: number, options?: SessionRequestOptions): Promise<SessionActionResponse> {
    const expected_revision = requireExpectedRevision(expectedRevision);
    let response: Response;
    try {
      response = await authorizedFetch(
        this.apiBaseUrl + '/events/sessions/' + encodeURIComponent(sessionId) + '/master-mode',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled, expected_revision }),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to update session mode');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to update session mode');
    return parseActionResponse(await response.json(), response.status, sessionId);
  }
}

// Export singleton instance
export const sessionService = new SessionService();
